import { Box, Typography, Grid, Button, Stack, CircularProgress, Alert, AlertTitle } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { ICarrinhoItem, TCriarPedido } from "..";
import { DadoValorPrinter } from "../../../../../../../components/BasicComponents/DisplayDados/DadoValorPrinter";
import { formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../../../../../helpers/masks";
import FormasPagamento from "./FormasPagamento";
import { Fragment, useContext, useMemo, useState, useEffect } from "react";
import { IInternFormCheckout } from "./FormasPagamento/DialogCadastrarValor";
import { IDataPublicos } from "../../../../../../../types/api/geral/pesquisar-publicos";
import { IItensPorLoja } from "../CarrinhoCard";
import DialogEditItem from "../CarrinhoCard/DialogEditItem";
import DialogConfirmarCriacao from "./DialogConfirmarCriacao";
import moment, { Moment } from "moment";
import FormasEntrega from "./FormasEntrega";
import SelectLojaComPesquisa, { TLoja } from "../../../../../../../components/SelectsSimpleApiQuery/SelectLojaComPesquisa";
import { AuthContext } from "../../../../../../../contexts/AuthContext";
import SelectCondicoesPagamento from "../../../../../../../components/SelectsSimpleApiQuery/SelectCondicoesPagamento";
import { IDataCondicoesPagamentoComercial } from "../../../../../../../types/api/comercial/pesquisar-condicoes-pagamento";
import DialogConfirmCondicoes from "./DialogConfirmCondicoes";
import { enqueueSnackbar } from "notistack";
import { IPesquisaVendedores } from "../../../../../../../types/api/geral/pesquisar-vendedores";
import { IPesquisaConsultores } from "../../../../../../../types/api/comercial/pesquisar-consultores";
import ListaItens from "./ListaItens";

export default function Checkout({
  itens,
  cliente,
  onRemoverItem,
  onEditarItem,
  isLoading,
  onConfirmar,
  handleClose,
  onEditarMultiplosItens
}: CheckoutProps) {
  const { userPerfilComLoja, getUserData } = useContext(AuthContext);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<IDataCondicoesPagamentoComercial | null>(null);
  const [openDialogEditItem, setOpenDialogEditItem] = useState<ICarrinhoItem | null>(null);
  const [openDialogConfirmar, setOpenDialogConfirmar] = useState(false);
  const userData = getUserData();

  const todosIdsBloqueados = userData?.perfis_vinculados.flatMap(
    (perfil) => perfil.condicoes_pagamento_blocked ?? []
  );

  const subTotal = itens.reduce((acc, cur) => {
    const preco = cur.preco ?? 0;
    const desconto = cur.desconto ?? 0;
    const quantidade = cur.quantidade ?? 0;
    return acc + (preco - desconto) * quantidade;
  }, 0);

  const descontoTotal = itens.reduce((acc, cur) => {
    const desconto = cur.desconto ?? 0;
    const quantidade = cur.quantidade ?? 0;
    return acc + desconto * quantidade;
  }, 0);

  const itensPorLoja = itens.reduce<Record<number, IItensPorLoja>>(
    (acc, item) => {
      const lojaId = item.loja.id;
      if (!acc[lojaId]) {
        acc[lojaId] = {
          loja: item.loja,
          itens: [],
        };
      }
      acc[lojaId].itens.push(item);
      acc[lojaId].itens.sort((a, b) => a.nome.localeCompare(b.nome));
      return acc;
    },
    {}
  );

  const itensPerloja = Object.values(itensPorLoja);
  //

  const methods = useForm<TCheckoutForm>({
    defaultValues: {
      sub_total: subTotal,
      valor_frete: "0",
      forma_entrega: "Balcão ou expedição",
      valoresAdicionados: [],
      lojas_ids: itensPerloja.map((item) => item.loja.id),
      cliente: cliente,
      comprador: cliente.nome_pessoa,
      observacao: "",
      previsao_entrega: null,
      loja_faturamento: userPerfilComLoja?.loja as TLoja,
      condicao_pagamento: null,
      parcelamento_maximo: 0
    },
  });
  const { watch, setValue, control } = methods;

  const valoresAdicionados = watch("valoresAdicionados");
  const valorFrete = formatarMonetárioParaNúmero(watch("valor_frete"));

  //Funções manipuladoras
  const onSubmit = (data: TCheckoutForm) => {
    //Fecha o modal de confirmação
    setOpenDialogConfirmar(false);

    //Cria o objeto de parametros das formas de pagamento
    const formas_recebimento = data.valoresAdicionados.map(
      (item: IInternFormCheckout) => {
        return {
          id: Number(item.forma_recebimento?.id) ?? 0,
          valor: formatarMonetárioParaNúmero(item.valor),
          parametros:
            item.pagamento.parcelas.length > 0
              ? item.pagamento
              : {
                ...item.pagamento,
                parcelas: [
                  {
                    valor: Number(
                      formatarMonetárioParaNúmero(item.valor).toFixed(2)
                    ),
                    vencimento: (item.pagamento?.intervalo > 1) ?
                      moment().add(item.pagamento?.intervalo, 'days').format("DD/MM/YYYY") :
                      moment().format("DD/MM/YYYY"),
                  },
                ],
              },
        };
      }
    );

    if (!data.condicao_pagamento) {
      return enqueueSnackbar("Selecione uma condição de pagamento valida", {
        variant: "error",
      });
    }

    const valorFrete = Number(formatarMonetárioParaNúmero(data.valor_frete || "0").toFixed(2));
    const valorAcrecimo = Number((data.valor_acrescimo - formatarMonetárioParaNúmero(data.valor_frete || "0")).toFixed(2));

    const params: TCriarPedido = {
      tipo_publico: cliente.tipo_publico,
      codigo_publico: cliente.codigo_publico,
      fazenda_inscricao: cliente.selected_fazenda?.inscricao ?? null,
      comprador: data.comprador,
      formas_recebimento: formas_recebimento,
      sub_total: Number(subTotal.toFixed(2)) + Number(descontoTotal.toFixed(2)),
      valor_acrescimo: valorAcrecimo,
      valor_desconto: Number(descontoTotal.toFixed(2)),
      valor_frete: valorFrete,
      previsao_entrega: data.previsao_entrega?.format("YYYY-MM-DD") ?? null,
      vendedor_id: Number(data.vendedor?.id || 0),
      consultor_id: Number(data.consultor?.id || 0),
      observacao: data.observacao,
      itens: itens.map((item) => ({
        id: item.id,
        quantidade: item.quantidade,
        preco: item.preco,
        loja_id: item.loja.id,
        desconto: item.desconto ?? 0,
        desconto_tipo: item.tipoDesconto === '%' ? 'Percentual' : 'Valor fixo',
        ...(item.praga ? { praga_id: item.praga.id } : {}),
        ...(item.cultura ? { cultura_id: item.cultura.id } : {}),
        acrescimo: 0,
        frete: 0
      })),
      loja_faturamento_id: data.loja_faturamento.id,
      condicao_pagamento_id: Number(data.condicao_pagamento?.id),
      possui_alerta: data.condicao_pagamento.alertas.length > 0,
    };

    onConfirmar(params);
  };

  const handleEdit = (item: ICarrinhoItem) => {
    setOpenDialogEditItem(item);
  };

  const valorRestante = useMemo(() => {
    const totalAtt = Number((subTotal + valorFrete).toFixed(2));
    const valoresRec = valoresAdicionados;
    let totalValores = 0;
    if (valoresRec.length > 0) {
      totalValores = Number(
        valoresRec
          .reduce(
            (acumulador: number, item: any) =>
              acumulador + formatarMonetárioParaNúmero(item.valor),
            0
          )
          .toFixed(2)
      );
    }
    setValue("valor_restante", totalAtt - totalValores);
    return totalAtt - totalValores;
  }, [subTotal, valoresAdicionados, valorFrete, setValue]);

  const valorAcrecimo = useMemo(() => {
    let valorAcrescimo = valorFrete ? valorFrete : 0;
    const valoresRec = valoresAdicionados.map((item: IInternFormCheckout) => {
      const parametrosExtras = valoresAdicionados
        .filter(
          (i) =>
            Array.isArray(i.pagamento.parcelas) &&
            i.pagamento.parcelas.length > 0
        )
        .map((i: IInternFormCheckout) => i.pagamento);
      return {
        id: item.forma_recebimento?.id,
        valor: formatarMonetárioParaNúmero(item.valor),
        parametros: parametrosExtras.length > 0 ? item.pagamento : null,
      };
    });
    valoresRec.forEach(({ valor, parametros }) => {
      if (
        parametros &&
        Array.isArray(parametros.parcelas) &&
        parametros.parcelas.length > 0
      ) {
        const valorParcelas = parametros.parcelas.reduce(
          (acc, cur) => acc + cur.valor,
          0
        );
        valorAcrescimo += valorParcelas - valor;
      }
    });

    setValue("valor_acrescimo", valorAcrescimo);
    return valorAcrescimo;
  }, [valoresAdicionados, valorFrete, setValue]);

  const formaEntrega = watch("forma_entrega");
  const previsaoEntrega = watch("previsao_entrega");
  const condicaoPagamento = watch("condicao_pagamento");

  const disabledBtn = useMemo(() => {
    if (!formaEntrega) return true;
    if (formaEntrega === "Transportadora" && (!previsaoEntrega)) return true;
    if (!condicaoPagamento) return true;
    return false;
  }, [formaEntrega, previsaoEntrega, condicaoPagamento]);

  useEffect(() => {
    const novoParcelamentoMaximo = itens.length > 0
      ? Math.min(...itens.map(item => item.setor?.maximo_parcelas ?? 0))
      : 0;
    setValue("parcelamento_maximo", novoParcelamentoMaximo);
  }, [itens]);

  return (
    <FormProvider {...methods}>
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid size={{xs: 12, md: 6}}>
            <Typography variant="h6" mb={2}>
              <b>Checkout</b>
            </Typography>
            <ListaItens
              itensPerloja={itensPerloja}
              onRemoverItem={onRemoverItem}
              onEditarItem={handleEdit}
              onEditItensCarrinho={onEditarMultiplosItens}
            />
          </Grid>
          <Grid container size={{xs: 12, md: 6}} spacing={2}>
            <Grid size={{xs: 12}}>
              <Typography variant="h6">
                <b>Formas de entrega</b>
              </Typography>
              <FormasEntrega />
            </Grid>
            <Grid size={{xs: 12}}>
              <Typography variant="h6" mb={2}>
                <b>Formas de pagamento</b>
              </Typography>
              <Grid container spacing={2} mb={1}>
                <Grid size={{xs: 12}}>
                  <Controller
                    control={control}
                    name="loja_faturamento"
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field }) => (
                      <SelectLojaComPesquisa
                        label="Loja de origem"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{xs: 12}}>
                  <Controller
                    control={control}
                    name="condicao_pagamento"
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field }) => (
                      <>
                        <SelectCondicoesPagamento
                          tipo_publico={cliente.tipo_publico}
                          codigo_publico={cliente.codigo_publico}
                          situacao_financeira={cliente.situacao_financeira}
                          produtor_rural={!!cliente.selected_fazenda}
                          loja_id={watch("loja_faturamento")?.id || null}
                          condicoes_pagamento_blocked={
                            todosIdsBloqueados || null
                          }
                          valor_total={subTotal + valorFrete}
                          status="Ativa"
                          autoSelectFirst
                          value={field.value}
                          onChange={(e) => {
                            if (valoresAdicionados.length > 0) {
                              setOpenConfirmDialog(e);
                              return;
                            }
                            field.onChange(e);
                          }}
                        />
                        {field.value && field.value.alertas.length > 0 && (
                          <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
                            <AlertTitle>Atenção:</AlertTitle>
                            {field.value.alertas?.map((item, index) => (
                              <li key={index}>{item} </li>
                            ))}
                          </Alert>
                        )}
                      </>
                    )}
                  />
                </Grid>
                <Grid size={{xs: 12}}>
                  <Stack direction='row' justifyContent='space-around'>
                    <DadoValorPrinter
                      alignValue='right'
                      alignTitle='right'
                      titulo="Subtotal"
                      valor={formatarValorMonetario(subTotal + descontoTotal)}
                    />
                    <DadoValorPrinter
                      alignValue='right'
                      alignTitle='right'
                      titulo="Descontos"
                      valor={formatarValorMonetario(descontoTotal)}
                    />
                    <DadoValorPrinter
                      alignValue='right'
                      titulo="Acréscimos"
                      valor={formatarValorMonetario(valorAcrecimo)}
                    />
                    <DadoValorPrinter
                      alignValue='right'
                      alignTitle='right'
                      titulo="Total"
                      valor={formatarValorMonetario(subTotal + valorAcrecimo)}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{xs: 12}}>
              <FormasPagamento cliente={cliente} subTotal={subTotal + valorFrete} />
            </Grid>
            <Grid size={{xs: 12}} sx={{ mt: 2 }}>
              <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
                <Button onClick={handleClose}>Fechar</Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!(valorRestante === 0) || isLoading || disabledBtn}
                  onClick={() => setOpenDialogConfirmar(true)}
                >
                  {isLoading && (
                    <CircularProgress size={15} sx={{ marginRight: 1 }} />
                  )}
                  Criar pedido
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {openDialogEditItem && (
        <DialogEditItem
          {...{ onEditarItem }}
          open={Boolean(openDialogEditItem)}
          onClose={() => setOpenDialogEditItem(null)}
          item={openDialogEditItem}
        />
      )}
      {openDialogConfirmar && (
        <DialogConfirmarCriacao
          open={openDialogConfirmar}
          handleClose={() => setOpenDialogConfirmar(false)}
          onSubmit={onSubmit}
        />
      )}
      {Boolean(openConfirmDialog) && (
        <DialogConfirmCondicoes
          open={Boolean(openConfirmDialog)}
          handleClose={() => setOpenConfirmDialog(null)}
          handleConfirmChange={() => {
            setOpenConfirmDialog(null);
            setValue("condicao_pagamento", openConfirmDialog);
            setValue("valoresAdicionados", []);
          }}
        />
      )}
    </FormProvider>
  );
}

interface CheckoutProps {
  itens: ICarrinhoItem[];
  cliente: IDataPublicos;
  onRemoverItem: (id: string, loja_id: number) => void;
  onEditarItem: (novoItem: ICarrinhoItem) => void;
  onEditarMultiplosItens: (novosItens: ICarrinhoItem[]) => void;
  isLoading: boolean;
  onConfirmar: (params: any) => void;
  handleClose: () => void;
}

export type TCheckoutForm = {
  valoresAdicionados: IInternFormCheckout[];
  lojas_ids: number[];
  cliente: IDataPublicos;
  comprador: string;
  observacao: string;
  vendedor: IPesquisaVendedores | null;
  consultor: IPesquisaConsultores | null;
  valor_acrescimo: number;
  forma_entrega: string;
  previsao_entrega: Moment | null;
  valor_frete: string;
  sub_total: number;
  valor_restante: number;
  loja_faturamento: TLoja;
  condicao_pagamento: IDataCondicoesPagamentoComercial | null;
  parcelamento_maximo: number;
};

export type TVendedor = {
  id: number;
  nome: string;
};